/**
 * Personal templates of a board, a database, a note, a Kanban column or a task
 * (`/account/templates`). They belong to the signed-in account only; applying one creates a NEW
 * independent resource, and later edits or deletion of the template never touch what was created
 * from it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiUpload, queryKeys } from '@/shared/api';

export type TemplateKind = 'board' | 'database' | 'document' | 'column' | 'task';

/** The order of the categories in Settings -> Templates. */
export const TEMPLATE_KINDS: readonly TemplateKind[] = [
  'board',
  'database',
  'document',
  'column',
  'task',
];

/** Kinds whose "save" can also keep the content (tasks / records); off by default. */
export const CONTENT_KINDS: readonly TemplateKind[] = ['board', 'database'];

export interface TemplateSummary {
  id: string;
  kind: TemplateKind;
  name: string;
  version: number;
  updated_at: string;
  /** The suggested title (task) or the column name (column). */
  headline: string;
  blocks: number;
  /** Kind-specific facts: board columns/tasks, database tables/columns/records, note files. */
  details: Record<string, number | boolean>;
}

export interface Template extends TemplateSummary {
  payload: Record<string, unknown>;
}

const templatesKey = (kind?: TemplateKind) => ['account', 'templates', kind ?? 'all'] as const;
const allTemplates = ['account', 'templates'] as const;

export function useTemplates(kind?: TemplateKind, enabled = true) {
  return useQuery({
    enabled,
    queryKey: templatesKey(kind),
    queryFn: ({ signal }) =>
      api.get<TemplateSummary[]>('/v1/account/templates', {
        signal,
        ...(kind && { query: { kind } }),
      }),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['account', 'templates', 'one', id] as const,
    queryFn: ({ signal }) => api.get<Template>(`/v1/account/templates/${id}`, { signal }),
  });
}

export function useUpdateTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      payload?: Record<string, unknown>;
      expected_version?: number;
    }) => api.patch<Template>(`/v1/account/templates/${id}`, data),
    onSuccess: () => client.invalidateQueries({ queryKey: allTemplates }),
  });
}

export function useDeleteTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/account/templates/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: allTemplates }),
  });
}

const SAVE_PATHS: Record<TemplateKind, string> = {
  task: 'cards',
  column: 'columns',
  board: 'boards',
  database: 'databases',
  document: 'documents',
};

/** Save an existing resource as a template (the server builds it from the stored object). */
export function useSaveAsTemplate(kind: TemplateKind, sourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; includeContent?: boolean }) =>
      api.post<Template>(`/v1/${SAVE_PATHS[kind]}/${sourceId}/save-as-template`, {
        name: input.name,
        include_content: input.includeContent ?? false,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: allTemplates }),
  });
}

export function useDuplicateTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Template>(`/v1/account/templates/${id}/duplicate`),
    onSuccess: () => client.invalidateQueries({ queryKey: allTemplates }),
  });
}

export interface AppliedTemplate {
  /** The new resource (a board, a database or a note). */
  resourceId: string;
  /** A retry of the same request found the resource the first one made. */
  reused: boolean;
}

const APPLY_PATHS: Partial<Record<TemplateKind, string>> = {
  board: 'boards-from-template',
  database: 'databases-from-template',
  document: 'documents-from-template',
};

/**
 * Create a board, database or note from a template. One request id per mutation call, so a retry
 * or a double click cannot create two resources.
 */
export function useApplyTemplate() {
  return useMutation({
    mutationFn: async (input: {
      kind: 'board' | 'database' | 'document';
      templateId: string;
      workspaceId: string;
      name?: string;
      sectionId?: string | null;
      groupId?: string | null;
    }): Promise<AppliedTemplate> => {
      const body = {
        template_id: input.templateId,
        request_id: crypto.randomUUID(),
        ...(input.name && { name: input.name }),
        ...(input.sectionId && { section_id: input.sectionId }),
        ...(input.groupId && { group_id: input.groupId }),
      };
      const path = `/v1/workspaces/${input.workspaceId}/${APPLY_PATHS[input.kind] ?? ''}`;
      if (input.kind === 'board') {
        const made = await api.post<{ board_id: string; reused: boolean }>(path, body);
        return { resourceId: made.board_id, reused: made.reused };
      }
      const made = await api.post<{ resource_id: string; reused: boolean }>(path, body);
      return { resourceId: made.resource_id, reused: made.reused };
    },
  });
}

export interface CardFromTemplate {
  card: { id: string; title: string };
  /** Label names the target board does not have (left out). */
  skipped_labels: string[];
}

export function useCreateCardFromTemplate(boardId: string, columnId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { templateId: string; title?: string }) =>
      api.post<CardFromTemplate>(`/v1/columns/${columnId}/cards-from-template`, {
        template_id: input.templateId,
        ...(input.title && { title: input.title }),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) }),
  });
}

export function useCreateColumnFromTemplate(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { templateId: string; name?: string }) =>
      api.post<{ id: string; name: string }>(`/v1/boards/${boardId}/columns-from-template`, {
        template_id: input.templateId,
        ...(input.name && { name: input.name }),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) }),
  });
}

export interface TemplateFile {
  media_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  is_image: boolean;
}

/** Adds a file to a document template being edited (the content then refers to it by id). */
export function uploadTemplateFile(templateId: string, file: File) {
  return apiUpload<TemplateFile>(`/v1/account/templates/${templateId}/media`, file, {
    filename: file.name,
  });
}

/** Where a document template serves its own files (owner only). */
export const templateMediaBase = (templateId: string) =>
  `/api/v1/account/templates/${encodeURIComponent(templateId)}/media`;
